/** WordPress の接続方法: 管理画面でアプリケーションパスワードを発行して貼る */
import {
  BrowserFrame, Callout, Checklist, Code, Mark, MockButton, MockInput, MockSidebarLayout,
  SectionTitle, Step, Steps, Trouble, UiLabel,
} from './parts'
import { ChannelRowMock, ConnectFormMock } from './postMocks'

export function WordPressGuide() {
  return (
    <div className="space-y-5">
      <Callout kind="warn" title="WordPress は準備中です（今は接続できません）">
        <p>
          WordPress 向けの記事を作る「ブログ機能」は準備中です。それに合わせて、ペルソナ画面の
          <UiLabel>WordPress を接続（準備中）</UiLabel> も今は押せません。
          このページは、使えるようになったときの手順の予告です。アプリケーションパスワードの発行（手順1）だけは先に済ませておけます。
        </p>
      </Callout>

      <Callout kind="info" title="WordPress は「アプリケーションパスワード」で接続します">
        <p>
          WordPress の管理画面で、Genemo Post 専用のパスワードを発行して貼ります。ふだんのログインパスワードは使いません。
          専用のパスワードは、いつでも WordPress 側で取り消せます。
        </p>
      </Callout>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="gm-card space-y-2 p-4">
          <h3 className="text-sm font-bold">準備するもの</h3>
          <Checklist items={[
            <>サイトの URL（<b>https</b> で始まるもの）</>,
            '投稿できる権限のある WordPress ユーザー（管理者・編集者・投稿者）',
            '所要時間の目安: 5分',
          ]} />
        </div>
        <div className="gm-card space-y-2 p-4">
          <h3 className="text-sm font-bold">知っておくこと</h3>
          <Checklist items={[
            'WordPress 5.6 以降に最初から入っている機能です。プラグインは要りません。',
            <>投稿方針で <b>下書きとして自動投入</b> を選べるのは WordPress だけです。</>,
            'セキュリティ系のプラグインが REST API やアプリケーションパスワードを止めていると接続できません。',
          ]} />
        </div>
      </div>

      <Steps>
        <Step n={1} title="管理画面でアプリケーションパスワードを発行する"
          mock={
            <BrowserFrame url="example.com/wp-admin/profile.php">
              <MockSidebarLayout menu={['ダッシュボード', '投稿', 'メディア', '固定ページ', 'ユーザー', '　プロフィール', '設定']} active="　プロフィール">
                <div className="font-bold">アプリケーションパスワード</div>
                <Mark n={1} block><div className="p-1"><MockInput label="新しいアプリケーションパスワード名" value="Genemo Post" /></div></Mark>
                <Mark n={2}><MockButton kind="primary">新しいアプリケーションパスワードを追加</MockButton></Mark>
                <div className="rounded-md border border-ok/40 bg-ok-bg p-2">
                  <div className="text-[11px]">新しいパスワード（この画面を離れると二度と表示されません）</div>
                  <Mark n={3} block><div className="px-1 font-mono font-bold">abcd EFGH 1234 ijkl MNOP 5678</div></Mark>
                </div>
              </MockSidebarLayout>
            </BrowserFrame>
          }>
          <p>
            WordPress の管理画面（<Code>https://あなたのサイト/wp-admin</Code>）に、投稿させたいユーザーでログインします。
            <UiLabel>ユーザー</UiLabel> → <UiLabel>プロフィール</UiLabel> を開き、下のほうの <b>アプリケーションパスワード</b> まで進みます。
          </p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>名前の欄に <b>Genemo Post</b> など、あとで分かる名前を入れます。</li>
            <li><UiLabel>新しいアプリケーションパスワードを追加</UiLabel> を押します。</li>
            <li>表示された <b>24文字のパスワード</b>（4文字ずつ空白で区切られています）をコピーします。空白はそのままで構いません。</li>
          </ol>
          <Callout kind="secret">このパスワードは画面を離れると二度と表示されません。他人に見せないでください。漏れたら同じ画面で取り消して作り直せます。</Callout>
        </Step>

        <Step n={2} title="Genemo Post に貼って接続する"
          mock={
            <div className="space-y-3">
              <ConnectFormMock n={4} fields={[
                { label: 'サイトURL (https://...)', value: 'https://example.com' },
                { label: 'ユーザー名', value: 'shop-admin' },
                { label: 'Application Password', value: 'abcd EFGH 1234 ijkl MNOP 5678', secret: true },
              ]} />
            </div>
          }>
          <p>ブログ機能の公開後に、Genemo Post の <UiLabel>ペルソナ</UiLabel> で、投稿させたいペルソナの <UiLabel>WordPress を接続</UiLabel> を押します（今は準備中で押せません）。</p>
          <ul className="list-disc space-y-1 pl-5">
            <li><b>サイトURL</b>: ブラウザのアドレス欄に出るとおりに入れます（<Code>https://</Code> や <Code>www.</Code> の有無も同じに）。<Code>/wp-admin</Code> は付けません。</li>
            <li><b>ユーザー名</b>: ログインに使うユーザー名です（ブログに表示される名前ではありません）。</li>
            <li><b>Application Password</b>: 手順1でコピーしたパスワードです。</li>
          </ul>
          <p><UiLabel>接続する</UiLabel> を押すと、Genemo Post がサイトに問い合わせて、その場で確かめます。</p>
        </Step>

        <Step n={3} title="「接続中」になったら、投稿方針を選ぶ"
          mock={<ChannelRowMock platform="WordPress" handle="https://example.com" policy="下書きとして自動投入" />}>
          <p>WordPress の行に <span className="gm-chip gm-chip-ok">接続中</span> が出れば完了です。右の投稿方針は、ブログ機能ができたときの出し方です。</p>
          <ul className="list-disc space-y-1 pl-5">
            <li><b>承認したものだけ投稿</b>: 承認キューで確認したものだけを公開します。</li>
            <li><b>下書きとして自動投入</b>: できた記事を WordPress の下書きに入れます。公開は WordPress 側で行います。</li>
            <li><b>完全自動投稿</b>: 安全チェックを通ったものを、承認を待たずに公開します。</li>
          </ul>
        </Step>
      </Steps>

      <SectionTitle>うまくいかないとき</SectionTitle>
      <Trouble items={[
        { symptom: 'プロフィールに「アプリケーションパスワード」の欄が無い', fix: 'サイトが https になっていないか、セキュリティ系のプラグイン（SiteGuard・Wordfence など）で止められています。プラグインの設定で REST API とアプリケーションパスワードを許可してください。' },
        { symptom: '「WordPress に接続できません: HTTP 401（Application Password・ユーザー名を確認してください）」', fix: 'ユーザー名かパスワードが違います。ユーザー名はログイン用の名前か確かめ、パスワードは作り直して貼ってください。' },
        { symptom: '「リダイレクトが返されました」', fix: 'サイトURL がアドレス欄の表示と違います（http と https、www の有無など）。ブラウザで開いたときの URL をそのまま入れてください。' },
        { symptom: '「接続できません。URL を確認してください」', fix: 'URL の打ち間違いか、サイトが外から見られない状態です。ブラウザでサイトが開けるか確かめてください。' },
      ]} />
    </div>
  )
}
